/** @jsxImportSource @emotion/react */
import {
  Box,
  BorderStyle,
  Color,
  Display,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  AlignItems,
} from '@gousto-internal/citrus-react'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { LinksList } from '../LinksList'
import type { SearchResultItem, SearchResult } from '../../types/entities'
import { useFetchCategories } from '../../hooks'
import { createSlugFromUrl } from '../../utils'
import { client } from '../../config/routes'
import { HelpCentre } from '../../common/tracking'
import { SearchShowMoreButton } from '../SearchShowMoreButton'

type SearchResultArticlesProps = {
  searchResult: SearchResult
  isFullResult?: boolean
  onShowMore?: () => void
  query: string
}

const SearchResultArticles = ({
  searchResult,
  isFullResult,
  onShowMore,
  query,
}: SearchResultArticlesProps) => {
  const { categories } = useFetchCategories()

  return (
    <>
      <LinksList>
        <Box
          paddingH={[0, 4]}
          bg={Color.White}
          borderStyle={BorderStyle.Solid}
          borderWidth={isFullResult ? 0.5 : 0}
          borderColor={Color.ColdGrey_100}
        >
          {searchResult?.results?.map((article: SearchResultItem, i) => {
            const {
              id, html_url: htmlUrl, title, section_id: sectionId, snippet,
            } = article
            const section = searchResult.sections.find((item) => item.id === sectionId)
            const category = categories?.find((item) => item.id === section?.category_id)
            const articleSnippet = DOMPurify.sanitize(snippet, { ALLOWED_TAGS: ['em'] })
            const articleNameSlug = createSlugFromUrl(htmlUrl)

            return (
              <Link
                key={id}
                to={`${client.article}/${articleNameSlug}/${id}`}
                css={{ display: 'block', textDecoration: 'none' }}
                data-tracking-action={HelpCentre.HELP_CENTRE_SEARCH_ARTICLE_CLICK}
                data-tracking-property={JSON.stringify({
                  search_term: query,
                  article_name: article.title,
                })}
              >
                <Box
                  display={Display.Flex}
                  justifyContent={JustifyContent.SpaceBetween}
                  paddingV={4}
                  paddingH={[4, 0]}
                  paddingRight={[8, 4]}
                  alignItems={AlignItems.Center}
                >
                  <Box
                    flexShrink={0}
                    width="100%"
                    paddingRight={2}
                  >
                    <Text size={1}>{category?.name}</Text>
                    <Text size={2} color={Color.ColdGrey_900} fontWeight={FontWeight.SemiBold}>
                      {title}
                    </Text>
                    <Box
                      data-testid={`snippet-html-${id}`}
                      css={{
                        direction: 'rtl',
                        textAlign: 'left',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                      color={Color.NeutralGrey_600}
                      dangerouslySetInnerHTML={{ __html: articleSnippet }}
                    />
                  </Box>
                  <Icon name="chevron_right" variant={IconVariant.Actionable} />
                </Box>
                <Box
                  borderStyle={BorderStyle.Solid}
                  borderColor={Color.ColdGrey_100}
                  borderBottomWidth={
                    isFullResult && i === searchResult.results.length - 1 ? 0 : 0.5
                  }
                />
              </Link>
            )
          })}
        </Box>
      </LinksList>
      {(!isFullResult && onShowMore) && (
        <SearchShowMoreButton
          query={query}
          onClick={onShowMore}
          text={searchResult.count > 3 ? 'Show more' : 'Go to search results'}
        />
      )}
    </>
  )
}

export { SearchResultArticles }
